import { getCSRFToken, dispatchToast, processResponseTriggers } from "../shared/htmx-helpers";
import type { CmsEditor, CMSEditorConfig } from "./editor";
import {
  CMS_REVERT_BTN_CLASS,
  CMS_REVERT_BTN_SVG,
  CMS_AI_BADGE_CLASS,
  CMS_AI_BADGE_SVG,
} from "./cms-field-badge";

// editorAiReview owns the assistant-edit surface: the on-screen revert
// buttons + "edited by assistant" badges, the live-apply of assistant field /
// list-cell writes (onCmsFieldFilled / onCmsListStructureChanged), the "AI
// edits (N)" review-list refresh/revert/accept flow, and the reconciliation
// helpers those share. Reaches the autosave queue, preview, dirty flag and
// left-pane refetch through `this`. Captures `config` for the assistant API
// base URL + context/slug it posts.
export function editorAiReview(config: CMSEditorConfig): Partial<CmsEditor> & ThisType<CmsEditor> {
  return {
    // Debounces the "AI edits (N)" review list refetch (Task 5.4) —
    // see _scheduleAiEditsRefresh.
    _aiEditsRefreshTimer: null as ReturnType<typeof setTimeout> | null,
    // Single-flight guard for _refreshAiEdits (see its comment): an
    // outerHTML swap of #cms-ai-edits detaches the old node mid-flight, so
    // two overlapping GETs can strand the panel half-rendered. _aiEditsRefreshing
    // marks a swap in flight; _aiEditsRefreshQueued records that another
    // refresh was requested while one was already running, so it reruns
    // exactly once after the in-flight swap settles instead of overlapping it.
    _aiEditsRefreshing: false,
    _aiEditsRefreshQueued: false,

    // _fieldValue reads the CURRENT value of a field by its short data-cms-field
    // key (the same key the revert button + autosave use).
    _fieldValue(field: string): string {
      const el = this._main()?.querySelector<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >(`[data-cms-field="${this._cssEscape(field)}"]`);
      return el?.value ?? "";
    },

    // _setFieldValue drives a field's underlying widget to `val` and fires the
    // same event the editor root listens for, so the normal autosave/preview
    // pipeline picks it up — UNLESS opts.suppress is set, in which case the
    // resulting event carries a one-shot data-cms-autosave-suppress flag
    // _onFieldEvent consumes without queuing an edit or touching the AI layer
    // (used by the on-screen assistant-fill path, which already persisted the
    // value server-side and must not re-arm autosave or clobber its own
    // just-recorded pre-AI snapshot). Generalizes the hidden-input (ColorField)
    // / <select> branches _onRevertFieldClick used to inline.
    _setFieldValue(field: string, val: string, opts: { suppress?: boolean } = {}) {
      const root = this._main();
      const el = root?.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        `[data-cms-field="${this._cssEscape(field)}"]`,
      );
      if (!el) return;
      if (el.dataset.cmsWidget === "markdown") {
        // MarkdownEditor: CM6 is the source of truth for the visible content;
        // the hidden input only mirrors it one-way (onChange). Drive the
        // Alpine component's setContent so the CodeMirror view itself
        // updates — a plain el.value assignment would desync the two.
        const host = el.closest("[x-data]");
        const data = host && (window as any).Alpine?.$data?.(host);
        if (data?.setContent) {
          data.setContent(val, opts);
        } else {
          (el as HTMLInputElement).value = val;
          if (opts.suppress) el.dataset.cmsAutosaveSuppress = "1";
          el.dispatchEvent(new CustomEvent("markdown-change", { bubbles: true }));
        }
        return;
      }
      if (el instanceof HTMLInputElement && el.type === "hidden") {
        // ColorField: drive its Alpine controller so the swatch + hex + hidden
        // input all update. setColor dispatches its own bubbling 'change'
        // (→ _onFieldEvent), so only mark suppress — don't also dispatch.
        const host = el.closest("[x-data]");
        const data = host && (window as any).Alpine?.$data?.(host);
        if (opts.suppress) el.dataset.cmsAutosaveSuppress = "1";
        if (data?.setColor) data.setColor(val);
        else {
          el.value = val;
          el.dispatchEvent(new Event("change", { bubbles: true }));
        }
        return;
      }
      // <select> (font/spacing/button/bool) or plain text/textarea input.
      if (opts.suppress) el.dataset.cmsAutosaveSuppress = "1";
      el.value = val;
      el.dispatchEvent(
        new Event(el instanceof HTMLSelectElement ? "change" : "input", { bubbles: true }),
      );
    },

    // _onRevertFieldClick pops the field's 2-level revert stack one level per
    // click (design spec §7.2): the assistant's pre-edit snapshot (data-cms-preai
    // — present only while that edit is unreviewed) first, then the published
    // baseline (data-cms-revert-published).
    _onRevertFieldClick(btn: HTMLElement) {
      const field = btn.dataset.cmsRevertField;
      if (!field) return;
      const cur = this._fieldValue(field);
      const preai = btn.dataset.cmsPreai; // undefined = no AI layer
      const pub = btn.dataset.cmsRevertPublished ?? "";
      let next: string | null = null;
      if (preai != null && cur !== preai)
        next = preai; // pop AI layer → pre-AI value
      else if (cur !== pub) next = pub; // pop → published
      if (next == null) return;
      this._setFieldValue(field, next); // UNsuppressed → change → _queueEdit → autosave (persists + server-clears the AI row)
      delete btn.dataset.cmsPreai; // AI layer resolved
      this._clearAiBadge(field);
    },

    // _clearAiBadge removes the "edited by assistant" badge for `field`, if
    // present. Called both when the revert button pops the AI layer
    // (_onRevertFieldClick) and when a genuine operator edit resolves it
    // (_clearAiLayerOnEdit) — the badge's lifetime == data-cms-preai's lifetime.
    _clearAiBadge(field: string) {
      const btn = this._main()?.querySelector<HTMLElement>(
        `[data-cms-revert-field="${this._cssEscape(field)}"]`,
      );
      const badge = btn?.parentElement?.querySelector<HTMLElement>("[data-cms-ai-badge]");
      badge?.remove();
    },

    // _clearAiLayerOnEdit runs on every genuine (non-suppressed) field edit
    // (_onFieldEvent). A no-op unless the field still carries an unresolved AI
    // layer.
    _clearAiLayerOnEdit(field: string) {
      const btn = this._main()?.querySelector<HTMLElement>(
        `[data-cms-revert-field="${this._cssEscape(field)}"]`,
      );
      if (!btn || btn.dataset.cmsPreai == null) return;
      delete btn.dataset.cmsPreai;
      this._clearAiBadge(field);
    },

    // _ensureRevertButton finds the field's revert button, creating it
    // (mirroring cms_revert_button.templ's markup) if the initial render
    // omitted it — a never-published field (DeployedText === "") has no
    // button until its first AI edit lands, which is exactly the case the
    // on-screen cms_field_filled path needs to handle.
    // NOTE: the button markup (class/svg) is single-sourced in
    // ./cms-field-badge (CMS_REVERT_BTN_CLASS / CMS_REVERT_BTN_SVG), which
    // mirrors templates/components/cms_revert_button.templ's cmsRevertButton —
    // change the visual in BOTH the templ constants and cms-field-badge.ts.
    _ensureRevertButton(field: string): HTMLElement | null {
      const root = this._main();
      if (!root) return null;
      const existing = root.querySelector<HTMLElement>(
        `[data-cms-revert-field="${this._cssEscape(field)}"]`,
      );
      if (existing) return existing;
      const input = root.querySelector<HTMLElement>(`[data-cms-field="${this._cssEscape(field)}"]`);
      const wrap = input?.closest(".relative") as HTMLElement | null;
      if (!wrap) return null;
      const btn = document.createElement("button");
      btn.setAttribute("type", "button");
      btn.dataset.cmsRevertField = field;
      btn.dataset.cmsRevertPublished = "";
      btn.className = CMS_REVERT_BTN_CLASS;
      const tip = this._msgs.revertTooltip || "";
      btn.setAttribute("data-tip", tip);
      btn.setAttribute("title", tip);
      btn.setAttribute("aria-label", tip);
      btn.innerHTML = CMS_REVERT_BTN_SVG;
      wrap.appendChild(btn);
      return btn;
    },

    // _showAiBadge ensures the "edited by assistant — review" badge is visible
    // for `field`, creating it beside the revert button if the initial render
    // didn't (AIEdited is false at render time for the field an on-screen
    // assistant edit is ABOUT to touch — that's the whole point of this
    // live-apply path). No-op if a badge already exists.
    // NOTE: the badge markup (class/svg) is single-sourced in
    // ./cms-field-badge (CMS_AI_BADGE_CLASS / CMS_AI_BADGE_SVG), which mirrors
    // templates/components/cms_revert_button.templ's cmsAiBadge — change the
    // visual in BOTH the templ constants and cms-field-badge.ts.
    _showAiBadge(field: string) {
      const btn = this._main()?.querySelector<HTMLElement>(
        `[data-cms-revert-field="${this._cssEscape(field)}"]`,
      );
      const wrap = btn?.parentElement;
      if (!wrap || wrap.querySelector("[data-cms-ai-badge]")) return;
      const tip = this._msgs.aiBadgeTooltip || "";
      const badge = document.createElement("span");
      badge.setAttribute("data-cms-ai-badge", "");
      badge.className = CMS_AI_BADGE_CLASS;
      badge.setAttribute("data-tip", tip);
      badge.setAttribute("title", tip);
      badge.setAttribute("aria-label", tip);
      badge.innerHTML = CMS_AI_BADGE_SVG;
      wrap.appendChild(badge);
    },

    // onCmsFieldFilled applies a just-written assistant edit to the DOM live
    // (no reload) when it's on-screen for the section the operator currently
    // has open. `field` on the payload is the FULL dictionary key
    // ("<context>/<section>/<field>", or a list-cell leaf
    // "<context>/<section>/<field>[i].<cell>"); `section` is compared against
    // the LIVE rail section (this.section), NOT just the server's on_screen
    // belief, because short DOM keys collide across sections and the rail can
    // switch client-side without the server's stamped target being refreshed
    // in time (constraints.md naming table — this gate is mandatory). A list-cell
    // edit takes the _applyListCellEdit branch below: it live-patches the on-screen
    // repeater cell AND its inline "edited by assistant" badge (design §8 inline
    // parity), the same as a scalar edit does for its field.
    onCmsFieldFilled(e: CustomEvent) {
      let d:
        | {
            field?: string;
            value?: string;
            pre_value?: string;
            context?: string;
            section?: string;
            on_screen?: boolean;
            on_page?: boolean;
          }
        | undefined;
      try {
        d = JSON.parse(e.detail?.data);
      } catch {
        return;
      }
      if (!d) return;
      // Every assistant field write — on-screen or not, scalar or list-cell —
      // adds/updates a row in the "AI edits (N)" review list (Task 5.3a), so
      // refresh it regardless of the on-screen gate below (that gate governs
      // ONLY the inline apply-to-DOM path further down).
      this._scheduleAiEditsRefresh();
      // Preview refresh is PAGE-scoped, not section-scoped. The default full-page
      // preview renders every section (rail nav just scrolls), so an edit to ANY
      // section of the current page (on_page) must refresh it — not only the
      // section on screen. In section mode the preview renders just the current
      // section, so only an on-screen (same-section) edit is visible there.
      // (on_screen implies on_page, so on-screen edits always refresh.)
      if (this._previewMode() === "full-page" ? d.on_page : d.on_screen) {
        this._schedulePreview();
      }
      if (!d.on_screen || !d.field || !d.section) return;
      if (d.section !== this.section) return;
      const leaf = d.field.slice(d.field.lastIndexOf("/") + 1);
      if (!leaf) return;
      // List-cell edit (e.g. "faq_items[1].answer"): apply the value to the
      // on-screen repeater cell + show its inline badge/revert (via
      // _applyListCellEdit → the controller's rows + aiCells). Applying it also
      // keeps the repeater's Alpine rows state in sync with the draft, so a later
      // manual edit of the repeater can't re-save a stale array and clobber this
      // change. (The preview was already scheduled above.)
      if (leaf.includes("[")) {
        this._applyListCellEdit(leaf, d.value ?? "", d.pre_value ?? "");
        return;
      }
      this._setFieldValue(leaf, d.value ?? "", { suppress: true });
      const btn = this._ensureRevertButton(leaf);
      if (btn) btn.dataset.cmsPreai = d.pre_value ?? "";
      this._showAiBadge(leaf);
      // This path bypasses _queueEdit (it's the suppressed/machine-fill path),
      // so it must recompute the revert button's visibility itself — otherwise
      // a published-untouched field's button stays hidden after an on-screen
      // AI edit (design spec §7.2 sequence 5).
      this._syncRevertButton(leaf, String(d.value ?? ""));
    },

    // onCmsListStructureChanged live-refreshes the on-screen list rows after
    // the assistant's add/remove-row tools (cms_add_list_item /
    // cms_remove_list_item, Tasks 6/7) splice a repeater's array leaf
    // server-side (Task 8 — the structural analogue of onCmsFieldFilled
    // above, which patches a single field/cell in place). A structural
    // change can't be patched in-place the way a cell edit can: every row
    // AFTER the touched index shifts its provenance key (items.N.cell ->
    // items.N-1.cell), so the only correct client response is a wholesale
    // re-fetch of the left pane via _refreshLeftPane() — it re-renders the
    // repeater's rows AND re-seeds AICells from the re-indexed store (§8
    // projection), rather than trying to reconcile indices client-side.
    // Every structural add/remove — on-screen or not — also adds/updates a
    // row in the "AI edits (N)" review list (same contract as
    // onCmsFieldFilled above), so _scheduleAiEditsRefresh() runs
    // unconditionally, before the on_screen gate below (that gate governs
    // ONLY the leftpane/inline re-render path via _refreshLeftPane()).
    // _refreshLeftPane() is gated on `on_screen` the same way
    // onCmsFieldFilled gates its inline apply: the payload has no
    // `field`/`section` DOM key to compare cell-by-cell, but `section` IS
    // compared against the LIVE rail section (this.section), not just the
    // server's on_screen belief — the rail can switch client-side before
    // the server's stamped target catches up (same rationale as
    // onCmsFieldFilled's section check above).
    onCmsListStructureChanged(e: CustomEvent) {
      let d:
        | {
            context?: string;
            section?: string;
            property_id?: string;
            on_screen?: boolean;
            on_page?: boolean;
          }
        | undefined;
      try {
        d = JSON.parse(e.detail?.data);
      } catch {
        return;
      }
      if (!d) return;
      this._scheduleAiEditsRefresh();
      // Preview is page-scoped (see onCmsFieldFilled): a structural add/remove on
      // ANY section of the current page changes the full-page render, so refresh
      // the preview on on_page in full-page mode; section mode shows only the
      // current section (on_screen). This replaces the old post-refreshLeftPane
      // schedule, which fired only for on-screen edits.
      if (this._previewMode() === "full-page" ? d.on_page : d.on_screen) {
        this._schedulePreview();
      }
      // Area Discover POI add/remove/edit (area_add_poi / area_remove_poi /
      // area_set_poi_fact) changes the POI board, which is a SIBLING of
      // #cms-editor-leftpane (so _refreshLeftPane() below never touches it) and
      // self-refreshes on the same area-poi-added/area-poi-removed body events the
      // operator's own board writes fire (poi-board.ts re-dispatches the server
      // HX-Trigger there). The assistant's write arrives only as this SSE event,
      // so bridge it — otherwise the POI manager + its count stay stale until a
      // reload. Gated on on_page (the board is a persistent sibling on every area
      // section, so keep it fresh for same-area edits even from another section).
      if (d.on_page && d.context === "area" && d.section === "discover") {
        document.body.dispatchEvent(new CustomEvent("area-poi-added"));
      }
      if (!d.on_screen || !d.section) return;
      if (d.section !== this.section) return;
      // _refreshLeftPane re-renders the repeater's rows AND re-seeds AICells from
      // the re-indexed store — the only correct response to a structural splice,
      // which shifts every later row's provenance key. Section-gated: it rewrites
      // the on-screen form, so it must match the section actually displayed.
      this._refreshLeftPane();
    },

    // _applyListCellEdit applies an assistant's repeater-cell write to the
    // on-screen repeater controller so the cell shows the new value immediately
    // (no reload), the repeater's rows stay in sync with the draft, AND the
    // inline "edited by assistant" badge + revert appear on that cell live. leaf
    // is a list-cell leaf like "faq_items[1].answer" → repeater field
    // "faq_items", row index 1, cell "answer". It sets rows[idx][cell]
    // reactively (x-model re-renders the input) + records the pre-value in the
    // controller's aiCells (its presence drives the inline badge; the value is
    // the revert target) but does NOT emit() — the assistant already persisted
    // the change via the draft overlay; the point is only to keep the on-screen
    // state fresh. No-op if the repeater/row isn't found (off-screen, reordered,
    // or the index drifted — a page reload reconciles from the draft).
    _applyListCellEdit(leaf: string, value: string, preValue: string) {
      const m = leaf.match(/^(.+)\[(\d+)\]\.(.+)$/);
      if (!m) return;
      const field = m[1];
      const idx = Number(m[2]);
      const cell = m[3];
      const el = this._main()?.querySelector<HTMLElement>(
        `[data-cms-repeater-field="${this._cssEscape(field)}"]`,
      );
      if (!el) return;
      const data = (
        window as { Alpine?: { $data?: (el: HTMLElement) => Record<string, unknown> } }
      ).Alpine?.$data?.(el);
      if (!data || !Array.isArray(data.rows) || !data.rows[idx]) return;
      (data.rows[idx] as Record<string, unknown>)[cell] = value;
      const aiCells = (data as { aiCells?: Record<string, string> }).aiCells;
      if (aiCells && typeof aiCells === "object") {
        aiCells[idx + "." + cell] = preValue;
      }
    },

    // _ackAiCell clears one repeater cell's server-side AI-edit row (POST
    // .../edits/ack) when the operator reverts or overrides it inline. Fired by
    // the repeater controller's cms-ai-cell-ack event (it holds no content path
    // / CSRF; this controller does). Addressed by (contentPath, leafPath) — the
    // ack writes no content, only DELETEs the provenance row (the operator's own
    // value / the revert's emit persist separately). Refreshes the "AI edits (N)"
    // review list so it drops the resolved row. Best-effort: a failed ack just
    // leaves the row for the review list (logged, no toast — this is a quiet
    // side effect of typing/reverting).
    _ackAiCell(leafPath: string) {
      if (!leafPath) return;
      const body = new URLSearchParams({ path: this._contentPath(), leaf_path: leafPath });
      fetch(`${config.baseURL}/api/assistant/edits/ack`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": getCSRFToken(),
        },
        body,
      })
        .then((resp) => {
          if (!resp.ok) throw new Error(`ack ${resp.status}`);
          this._scheduleAiEditsRefresh();
        })
        .catch((err) => console.error("cms ai cell ack failed", err));
    },

    // onAutosaveFlushed reaches the live editor via the module-scoped window
    // listener registered in init() (see cms-autosave-flushed above). Fired
    // after EVERY draft save — a normal field autosave (_flush) or a
    // review-list revert (_afterAssistantRevert, which dispatches this same
    // event since raw fetch() never auto-processes an HX-Trigger response
    // header) — so the "AI edits (N)" review list stays live either way.
    onAutosaveFlushed() {
      this._scheduleAiEditsRefresh();
    },

    // _scheduleAiEditsRefresh debounces a GET of the "AI edits (N)" review
    // list partial (#cms-ai-edits, Task 5.3a) so it reflects the latest
    // assistant writes / reverts without a manual reload (Task 5.4). The
    // panel that MOUNTS #cms-ai-edits is Task 6.2 — guard for it not
    // existing yet (unconfigured assistant, or this landing before 6.2): a
    // missing element is a silent no-op, never an error.
    _scheduleAiEditsRefresh() {
      if (!document.getElementById("cms-ai-edits")) return;
      if (this._aiEditsRefreshTimer) clearTimeout(this._aiEditsRefreshTimer);
      this._aiEditsRefreshTimer = setTimeout(() => this._refreshAiEdits(), 500);
    },
    // _refreshAiEdits is single-flight against ITSELF: _afterAssistantRevert
    // (via cms-autosave-flushed -> onAutosaveFlushed) and a manual field edit
    // can each schedule a refresh close enough together that two outerHTML
    // swaps of #cms-ai-edits overlap. Since an outerHTML swap detaches the
    // OLD #cms-ai-edits node, htmx:afterSwap fires on the NEW node and
    // htmx:swapError falls back to document.body — a listener bound to the
    // node we started from would never see either, permanently wedging any
    // node-scoped lock. So the guard is driven off the Promise htmx.ajax()
    // itself returns (resolves once the swap settles, rejects on error) —
    // node-independent by construction. A refresh requested while one is
    // already in flight is queued (not dropped, not started concurrently)
    // and replayed exactly once after the in-flight swap finishes.
    _refreshAiEdits() {
      if (!document.getElementById("cms-ai-edits")) return;
      const htmx = (window as any).htmx;
      if (!htmx) return;
      if (this._aiEditsRefreshing) {
        this._aiEditsRefreshQueued = true;
        return;
      }
      this._aiEditsRefreshing = true;
      const done = () => {
        this._aiEditsRefreshing = false;
        if (this._aiEditsRefreshQueued) {
          this._aiEditsRefreshQueued = false;
          this._refreshAiEdits();
        }
      };
      // Pass the viewer's current page so BuildCMSAIEditReviewData marks group.Current
      // correctly — otherwise the "go to page" link wrongly shows for the page you're
      // already on until a full navigation recomputes it.
      const q = new URLSearchParams({ context: config.context || "", slug: config.slug || "" });
      htmx
        .ajax("GET", `${config.baseURL}/api/assistant/edits?${q}`, {
          target: "#cms-ai-edits",
          swap: "outerHTML",
        })
        .catch(() => {
          // Swap failed (e.g. swapError) — the panel may be stale rather
          // than stranded; re-schedule through the normal debounced path
          // rather than retrying immediately in a tight loop.
          this._scheduleAiEditsRefresh();
        })
        .finally(done);
    },

    // onReviewRevertClick reverts ONE review-list row back to its
    // pre-assistant value (Task 5.3b's POST .../edits/revert). Sent as
    // application/x-www-form-urlencoded — mirrors wizard-autosave.ts's own
    // raw-fetch POST pattern, and matches handler_cms_assistant_test.go's
    // newFormRequest helper, which is the shape the review list's revert
    // buttons are documented to submit. api.Bind[T]'s revertCMSAIEditRequest
    // only declares key/path/property_id — NOT the row's leaf_path (the
    // server recomputes it from key+section) — so data-cms-revert-leaf is
    // deliberately not sent here even though the row carries it.
    async onReviewRevertClick(btn: HTMLElement) {
      const key = btn.dataset.cmsRevertKey;
      if (!key) return;
      const path = btn.dataset.cmsRevertPath || "";
      const body = new URLSearchParams(path ? { key, path } : { key });
      try {
        const resp = await fetch(`${config.baseURL}/api/assistant/edits/revert`, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": getCSRFToken(),
          },
          body,
        });
        if (!resp.ok) throw new Error(`revert ${resp.status}`);
        // Surface the server's "reverted" confirmation toast: a raw fetch()
        // never auto-processes the HX-Trigger response header (unlike an
        // htmx-driven request), so re-dispatch it here — the same pattern
        // poi-board.ts / curation-modal.ts / image-picker.ts already use.
        processResponseTriggers(resp);
        // A POI-override cell (area/discover/item_overrides.<factID>.<cell>) lives
        // in the POI board, not the left form — so _afterAssistantRevert skips the
        // (error-prone, unnecessary) _refreshLeftPane for it. Every other field —
        // including area scalar fields (cta/map/intro headings) shown in the left
        // form — needs the form re-seeded.
        this._afterAssistantRevert(key.includes("item_overrides."));
      } catch (err) {
        console.error("cms ai edit revert failed", err);
        dispatchToast(this._msgs.saveFailed, "error");
      }
    },

    // onReviewRevertStructuralClick inverts ONE structural add/remove row
    // (Task 9's revert-structural branch on the SAME POST
    // .../edits/revert endpoint — server dispatches on structural_leaf vs.
    // key). Mirrors onReviewRevertClick's fetch shape exactly, just with
    // structural_leaf instead of key (data-cms-revert-path is still the
    // row's content path, required to disambiguate the leaf across
    // properties — see RevertCMSAIEditStructural's doc comment).
    async onReviewRevertStructuralClick(btn: HTMLElement) {
      const structuralLeaf = btn.dataset.cmsRevertStructural;
      if (!structuralLeaf) return;
      const path = btn.dataset.cmsRevertPath || "";
      const body = new URLSearchParams({ structural_leaf: structuralLeaf, path });
      try {
        const resp = await fetch(`${config.baseURL}/api/assistant/edits/revert`, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": getCSRFToken(),
          },
          body,
        });
        if (!resp.ok) throw new Error(`revert ${resp.status}`);
        // Re-dispatch the server HX-Trigger so the "reverted" toast fires (raw
        // fetch never auto-processes it).
        processResponseTriggers(resp);
        this._afterAssistantRevert();
      } catch (err) {
        console.error("cms ai edit structural revert failed", err);
        dispatchToast(this._msgs.saveFailed, "error");
      }
    },

    // onReviewRevertAllClick reverts EVERY recorded AI-edit row for this
    // website (Task 5.3b's POST .../edits/revert-all — the review list
    // header's "Revert all" control).
    async onReviewRevertAllClick() {
      try {
        const resp = await fetch(`${config.baseURL}/api/assistant/edits/revert-all`, {
          method: "POST",
          headers: { "X-CSRF-Token": getCSRFToken() },
        });
        if (!resp.ok) throw new Error(`revert-all ${resp.status}`);
        // Re-dispatch the server HX-Trigger so the "reverted all" toast fires
        // (raw fetch never auto-processes it).
        processResponseTriggers(resp);
        this._afterAssistantRevert();
      } catch (err) {
        console.error("cms ai edit revert-all failed", err);
        dispatchToast(this._msgs.saveFailed, "error");
      }
    },

    // onReviewAcceptClick accepts ONE review-list row (Task 6's POST
    // .../edits/ack) — clears its provenance row WITHOUT touching content.
    // Sent as application/x-www-form-urlencoded, mirroring
    // onReviewRevertClick / _ackAiCell above. Quiet: no success toast (accept
    // is a no-op from the operator's perspective — the value they already
    // saw is simply no longer flagged).
    async onReviewAcceptClick(btn: HTMLElement) {
      const path = btn.dataset.cmsAcceptPath || "";
      const leafPath = btn.dataset.cmsAcceptLeaf || "";
      if (!leafPath) return;
      const body = new URLSearchParams({ path, leaf_path: leafPath });
      try {
        const resp = await fetch(`${config.baseURL}/api/assistant/edits/ack`, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": getCSRFToken(),
          },
          body,
        });
        if (!resp.ok) throw new Error(`ack ${resp.status}`);
        this._afterAiEditsAccepted();
      } catch (err) {
        console.error("cms ai edit accept failed", err);
        dispatchToast(this._msgs.saveFailed, "error");
      }
    },

    // onReviewAcceptAllClick accepts EVERY recorded AI-edit row for this
    // website (Task 6's POST .../edits/accept-all — the review list header's
    // "Accept all" control). Like onReviewAcceptClick, clears provenance only
    // — no content changes, no success toast here (accept-all's confirming
    // toast, if any, comes from the endpoint's own dispatch — see
    // assistant.edits.accepted_all).
    async onReviewAcceptAllClick() {
      try {
        const resp = await fetch(`${config.baseURL}/api/assistant/edits/accept-all`, {
          method: "POST",
          headers: { "X-CSRF-Token": getCSRFToken() },
        });
        if (!resp.ok) throw new Error(`accept-all ${resp.status}`);
        // Re-dispatch the server HX-Trigger so the "accepted all" toast fires
        // (raw fetch never auto-processes it). Accept-all sends only the toast
        // (no draft-saved event), so this has no other side effect.
        processResponseTriggers(resp);
        this._afterAiEditsAccepted();
      } catch (err) {
        console.error("cms ai edit accept-all failed", err);
        dispatchToast(this._msgs.saveFailed, "error");
      }
    },

    // _afterAiEditsAccepted runs after a per-row / bulk ACCEPT (ack / accept-all).
    // Accept only drops provenance rows — the draft content is unchanged — so it
    // must NOT flip dirty or refresh the preview. It only clears the on-screen
    // badges (via the left-pane refetch) and the review list.
    _afterAiEditsAccepted() {
      this._refreshLeftPane();
      document.body.dispatchEvent(
        new CustomEvent("cms-autosave-flushed", {
          detail: { target: "assistant:accept" },
          bubbles: true,
        }),
      );
    },

    // _afterAssistantRevert reconciles the UI after a server-side review-list
    // revert (single leaf / structural / "Revert all"), which changed content +
    // cleared provenance but never touched the on-screen inputs/icons.
    // poiOverrideOnly=true means the reverted leaf was an area Discover POI cell
    // (item_overrides.<factID>.<cell>): those live in the POI board, NOT the left
    // form, so skip _refreshLeftPane (redundant, and its full-page GET +
    // #cms-editor-leftpane select swap has thrown an htmx swapError from the
    // manage-mode/board takeover state). Every other reverted field — including
    // area SCALAR fields (cta/map/intro headings) shown in the left form — needs
    // the form re-seeded so its input value + revert/AI icons stop lingering.
    // Dispatching cms-autosave-flushed (rather than calling
    // _scheduleAiEditsRefresh directly) reuses the SAME debounced pathway
    // deliverable 1 wires for cms_field_filled, and is a harmless no-op for
    // the image-picker's OTHER listener on this event — it gates on an
    // exact detail.target match, and "assistant:revert" never matches a
    // real picker target.
    _afterAssistantRevert(poiOverrideOnly = false) {
      this._setDirty(true);
      this._schedulePreview();
      if (!poiOverrideOnly) {
        this._refreshLeftPane();
      }
      // On an area page also refresh the POI board (a sibling of the left form):
      // it self-refreshes on area-poi-* body events (poi-board.ts), so a reverted
      // POI cell shows its restored value. Harmless for a non-POI area revert —
      // the board just refetches.
      if (config.context === "area") {
        document.body.dispatchEvent(new CustomEvent("area-poi-added"));
      }
      document.body.dispatchEvent(
        new CustomEvent("cms-autosave-flushed", {
          detail: { target: "assistant:revert" },
          bubbles: true,
        }),
      );
    },

    // _afterOperatorRowRemoved runs after repeater.ts's cms-row-removed event
    // (Task 8B): a persisted repeater row was deleted server-side via the
    // remove-row endpoint (the transactional splice + provenance re-index
    // chokepoint). The draft overlay changed, same as any other autosave, so
    // mark dirty + schedule the preview. Unlike a normal field edit, the
    // deletion may have shifted OTHER rows' aiCells/publishedCells indices
    // (ReindexOnRemoveOn re-keys the surviving scalar provenance server-side)
    // — the repeater's own local badge maps don't know that happened, so
    // refresh the leftpane to re-seed rows/aiCells/publishedCells from the
    // server, mirroring _afterAssistantRevert.
    _afterOperatorRowRemoved() {
      this._setDirty(true);
      this._schedulePreview();
      this._refreshLeftPane();
      document.body.dispatchEvent(
        new CustomEvent("cms-autosave-flushed", {
          detail: { target: "operator:remove-row" },
          bubbles: true,
        }),
      );
    },

    // _syncRevertButton shows a field's revert button when there's anything
    // below the current value to pop back to — the published baseline
    // (data-cms-revert-published) OR, while an AI edit is unreviewed, the
    // pre-AI snapshot (data-cms-preai) — hides it otherwise (design spec
    // §7.2). Called on every field edit (_queueEdit) so the button reappears
    // if the operator re-edits a field they had just reverted.
    _syncRevertButton(field: string, value: string) {
      const btn = this._main()?.querySelector<HTMLElement>(
        `[data-cms-revert-field="${this._cssEscape(field)}"]`,
      );
      if (!btn) return;
      const pub = btn.dataset.cmsRevertPublished ?? "";
      const preai = btn.dataset.cmsPreai;
      const show = (pub !== "" && value !== pub) || (preai != null && value !== preai);
      btn.style.display = show ? "" : "none";
    },
  };
}
